/** Shell sidebar: project switcher, current project's pages, workspace pages, docs links */

import { Link, useLocation } from 'react-router-dom'
import { ExternalLink, X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useCurrentProjectSlug } from '../../hooks/use-current-project'
import { useT } from '../../i18n'
import { AgentWikiLogo } from '../brand/agentwiki-logo'
import { DOCS_NAV_ITEMS, WORKSPACE_NAV_ITEMS, activeNavTarget, projectNavItems, projectPath, type NavItem } from './nav-config'
import { LanguageSwitcher, ThemeSwitcher } from './preference-switchers'
import { ProjectSwitcher } from './project-switcher'

interface ShellSidebarProps {
  /** Rendered inside the mobile drawer: shows a close button */
  onClose?: () => void
}

const sectionHeadingClass = 'px-2 pb-1 text-xs font-semibold text-fg-subtle'

const linkBaseClass = 'flex min-h-9 items-center gap-2.5 rounded-md px-2 text-sm'

function navLinkClass(isActive: boolean) {
  return cn(linkBaseClass, isActive ? 'bg-accent-soft font-medium text-accent-text' : 'text-fg-muted hover:bg-surface-hover hover:text-fg')
}

export function NavList({ items, label }: { items: NavItem[]; label: string }) {
  const t = useT()
  const { pathname } = useLocation()
  const active = activeNavTarget(items, pathname)
  return (
    <ul aria-label={label} className="space-y-0.5">
      {items.map(({ labelKey, to, icon: Icon, external }) => (
        <li key={to}>
          {external ? (
            <a href={to} target="_blank" rel="noopener noreferrer" className={navLinkClass(false)}>
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate">{t(labelKey)}</span>
              <ExternalLink className="ml-auto h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-label={t('nav.opensInNewTab')} />
            </a>
          ) : (
            <Link to={to} aria-current={to === active ? 'page' : undefined} className={navLinkClass(to === active)}>
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="truncate">{t(labelKey)}</span>
            </Link>
          )}
        </li>
      ))}
    </ul>
  )
}

export function ShellSidebar({ onClose }: ShellSidebarProps) {
  const t = useT()
  const projectSlug = useCurrentProjectSlug()

  return (
    <div className="flex h-full w-full flex-col bg-surface-muted text-fg">
      <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4">
        <Link to={projectPath(projectSlug)} aria-label="AgentWiki" className="rounded-md">
          <AgentWikiLogo />
        </Link>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('shell.closeMenu')}
            className="cursor-pointer rounded-md p-2 text-fg-subtle hover:bg-surface-hover hover:text-fg"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-6 overflow-y-auto overflow-x-hidden px-2 py-4" aria-label={t('shell.navigation')}>
        <section className="space-y-2">
          <ProjectSwitcher />
          <NavList items={projectNavItems(projectSlug)} label={t('nav.project')} />
        </section>

        <section>
          <h2 className={sectionHeadingClass}>{t('nav.workspace')}</h2>
          <NavList items={WORKSPACE_NAV_ITEMS} label={t('nav.workspace')} />
        </section>

        <section>
          <h2 className={sectionHeadingClass}>{t('nav.docs')}</h2>
          <NavList items={DOCS_NAV_ITEMS} label={t('nav.docs')} />
        </section>
      </nav>

      {/* The topbar hides its switchers on narrow screens; the drawer carries them instead */}
      {onClose && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border px-4 py-3">
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
      )}
    </div>
  )
}
